import axios, { InternalAxiosRequestConfig } from 'axios';
import React from 'react';

import SnackbarWithLinks from 'components/SnackbarWithLinks/SnackbarWithLinks';
import { appConfig } from 'constants/appConfig';
import {
  alphaSessionToken,
  expireAlphaPopupSession,
  inAlphaPopup,
} from 'services/alpha/alphaSession';
import { signOut } from 'utils/session';
import { enqueueSnackbar, SnackbarKey, SnackbarOrigin } from 'notistack';
import { LOGIN } from 'router/routes';

interface ISnackbarWithLinksProps {
  id: SnackbarKey;
  message: string | number | boolean | React.ReactElement | Iterable<React.ReactNode>;
  variant: 'error';
  persist: boolean;
  style: React.CSSProperties;
  anchorOrigin: SnackbarOrigin;
  hideIconVariant: boolean;
  iconVariant: Record<string, React.ReactNode>;
}

const axiosInstance = axios.create({
  baseURL: appConfig.baseUrl,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const accessToken = alphaSessionToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

// The extension build has no server to resolve a real navigation to LOGIN
// (the side panel is a single static document), so it registers an in-app
// handler here instead of relying on the window.location.replace below.
// The hosted app never calls this, so its behavior is unchanged.
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  onUnauthorized = handler;
};

axiosInstance.interceptors.response.use(
  res => res,
  res => {
    if (res?.response?.status === 401 && res?.config?.url !== '/Token') {
      if (inAlphaPopup()) {
        expireAlphaPopupSession();
        return Promise.reject(res);
      }

      signOut();

      if (onUnauthorized) {
        onUnauthorized();
        return Promise.reject(res);
      }

      // Prefix LOGIN with the Vite base so the redirect stays inside the pod sub-path.
      // (process.env.PUBLIC_URL is undefined under Vite — use import.meta.env.BASE_URL.)
      const podBase = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
      window.location.replace(`${podBase}${LOGIN}`);

      return Promise.reject(res);
    }

    const errorConfig = res?.config as {
      skipErrorSnackbar?: boolean;
      rejectWithResponse?: boolean;
    } | undefined;

    if (errorConfig?.rejectWithResponse) {
      return Promise.reject(res);
    }

    // Allow individual requests to opt out of the global error snackbar by setting
    // `skipErrorSnackbar: true` on the request config. Used by features that surface
    // the error in their own UI surface (e.g. the CallScribe softphone, where the
    // tier-gating 503 is expected in dev environments without Twilio configured).
    if (errorConfig?.skipErrorSnackbar) {
      return;
    }

    const errorMessage = res?.response?.data?.message || 'An error occurred';

    enqueueSnackbar(errorMessage, {
      variant: 'error',
      autoHideDuration: 8000,
      content: (key, message) => {
        const props: ISnackbarWithLinksProps = {
          id: key,
          message: message ?? errorMessage,
          variant: 'error',
          persist: false,
          style: {},
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          hideIconVariant: false,
          iconVariant: {},
        };
        return React.createElement(SnackbarWithLinks, props);
      },
    });

    // MUST re-reject: an interceptor's rejection handler that returns a plain
    // value (as both branches above used to, implicitly, via a bare `return`)
    // makes axios treat the request as RECOVERED — every failed API call in
    // the app was silently "succeeding" with `undefined` data. Callers doing
    // `.then(res => res.data)` (e.g. the login thunks) then threw a TypeError
    // reading .data off undefined, which createAsyncThunk serializes into a
    // plain object, so `.unwrap()` throws something that is not an Error
    // instance either way. See MSPPROC-17664.
    return Promise.reject(res);
  },
);

export default axiosInstance;
